import FullScreenImage from '@components/atoms/images/fullScreenImage';
import PauseAdInfo from '@components/elements/pauseAds/pauseAdInfo';
import PauseAdContainer from '@components/atoms/container/pauseAdContainer';
import PauseAdKeepWatchingText from '@components/atoms/text/pauseAdKeepWatchingText';
import PauseAdPauseIconContainer from '@components/atoms/container/pauseAdPauseIconContainer';
import PauseAdText from '@components/atoms/text/pauseAdText';
import PauseIcon from '@components/atoms/icons/pause';
import TranslationManager from '@shared/assets/locale/translationsManager';
import { adsSelector } from '@shared/store/slices/ads.slice';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import AnalyticsInstance, { AnalyticEventType } from '@shared/utils/analytics';
import { useEffect, useRef } from 'react';
import Focus from '@shared/types/enums/focus';
import { setFocus } from '@shared/store/slices/focus.slice';

/**
 * Component that displays pause ads when the player is paused.
 * Shows ad background image, event information, pause icon, and keep watching text.
 * Handles keyboard interactions to resume playback or open floating menu.
 *
 * @returns {JSX.Element} Pause ad with full-screen ad and event information
 */
export default function PauseAd() {
  const { pauseAd } = useAppSelector(adsSelector);
  const pauseAdRefActive = useRef(false);
  const { asset, background } = pauseAd || {};
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (pauseAd) {
      setTimeout(() => {
        dispatch(setFocus(Focus.PAUSE_ADS));
      });
    }
  }, []);

  useEffect(() => {
    if (pauseAd) {
      pauseAdRefActive.current = true;
    } else {
      if (pauseAdRefActive.current) {
        AnalyticsInstance.addEvent(AnalyticEventType.PAUSE_AD_DISMISS);
      }
      pauseAdRefActive.current = false;
    }
  }, [pauseAd]);

  const onLoad = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const target = event.target as HTMLImageElement;
    target.style.opacity = '1';
    AnalyticsInstance.getOmnitrackManager()
      ?.getPauseAdSession()
      ?.setWasAdShown(true);
    AnalyticsInstance.addEvent(AnalyticEventType.PAUSE_AD_RESPONSE);
  };

  const onError = () => {
    AnalyticsInstance.getOmnitrackManager()
      ?.getPauseAdSession()
      ?.setWasAdShown(false);
    AnalyticsInstance.addEvent(AnalyticEventType.PAUSE_AD_RESPONSE);
  };
  return pauseAd ? (
    <PauseAdContainer>
      <FullScreenImage src={background} onLoad={onLoad} onError={onError} />
      <PauseAdInfo asset={asset} />
      <PauseAdPauseIconContainer>
        <PauseIcon height={55} width={34} />
      </PauseAdPauseIconContainer>
      <PauseAdKeepWatchingText>
        {TranslationManager.get('COMPONENTS.RAILS.TITLE.KEEP_WATCHING')}
      </PauseAdKeepWatchingText>
      <PauseAdText>
        {TranslationManager.get('COMPONENTS.ADS.GENERIC')}
      </PauseAdText>
    </PauseAdContainer>
  ) : null;
}
